import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { Mapping } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useGetAppointmentColorsEnabled } from '@frontend/schedule-helpers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { Chip, Text } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { SETTINGS_TRACKING_IDS } from '../../trackingIds/settings-tracking-ids';
import { ColorDot } from '../appointment-panel/color-dot';
import { CompactTable } from '../compact-table/compact-table';
import { VisibilityToggle } from '../ManagePractitionersV3/VisibilityToggle';
import { ProvidersChip } from './providers-chip';
import { AppointmentTypesTableProps } from './types';

type AppointmentTypesCompactTableProps = AppointmentTypesTableProps;

export const AppointmentTypesCompactTable = ({
  locationId,
  pageConfig,
  isLoading,
  debouncedSearchValue,
  setSelectedAppointmentTypes,
  bulkActions,
  rowActions,
  setPageConfig,
  mappingList,
}: AppointmentTypesCompactTableProps) => {
  const { t } = useTranslation('scheduleAppointmentTypes');
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();
  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const isOnlineSchedulingEnabled = getCustomizationFlagValue('onlinescheduling');

  const appointmentTypeInfiniteQuery = SchedulerV3.Helpers.useGetAppointmentTypeInfiniteListForV3({
    searchText: debouncedSearchValue,
    pageLimit: pageConfig.limit,
    locationId,
  });
  const { isLoading: isLoadingAppointmentTypesData } = appointmentTypeInfiniteQuery;
  const handleTableRefetch = useCallback(() => {
    appointmentTypeInfiniteQuery.refetch();
  }, [appointmentTypeInfiniteQuery]);

  const handleSearchField = useCallback(
    (searchValue: string) => {
      const trimmedSearchValue = searchValue.trim();
      if (trimmedSearchValue.length !== 1) {
        setPageConfig((prev) => ({ ...prev, page: 1, searchValue: trimmedSearchValue }));
      }
    },
    [setPageConfig]
  );

  const tableActions = useMemo(
    () => [
      {
        Icon: () => <Icon name='update' size={24} />,
        label: t('Refetch'),
        onClick: handleTableRefetch,
        type: 'icon',
      },
    ],
    [handleTableRefetch, t]
  );

  return (
    <CompactTable
      infiniteQueryProps={appointmentTypeInfiniteQuery}
      isLoading={isLoading || isLoadingAppointmentTypesData}
      tableId='appointment-type-compact-table'
      rowElement={(appointmentType: AppointmentType) => <CompactTable.RowHeader title={appointmentType.displayName} />}
      expandedRowElement={(appointmentType: AppointmentType) => (
        <ExpandedRowElement
          appointmentType={appointmentType}
          isOnlineSchedulingEnabled={isOnlineSchedulingEnabled}
          canUseAppointmentColors={canUseAppointmentColors}
          mappingList={mappingList}
        />
      )}
      toolbarElement={<CompactTable.Toolbar />}
      rowActions={rowActions}
      bulkActions={bulkActions}
      isSelectable={canUseAppointmentColors}
      isRowDisabled={(row: AppointmentType) => isIntegratedOffice && !row.details?.act}
      rowSelectionConfig={{
        isRowDisabled(rowData) {
          return !rowData.details?.act;
        },
        onSelectionToggle(data) {
          setSelectedAppointmentTypes(data);
        },
      }}
      listHeight={400}
      globalSearchConfig={{
        initialValue: '',
        placeholder: t('Search Appointment Types Name'),
        searchHandler: handleSearchField,
      }}
      tableActions={tableActions}
      {...(!isLoading && {
        emptyStateConfig: {
          type: 'schedule',
          ...(!debouncedSearchValue && {
            header: '',
            description: (
              <div css={{ textAlign: 'center' }}>
                <Text color='subdued'>{t('Ready to start scheduling?')}</Text>
                <Text color='subdued'>{t('Set up your first appointment type to start taking bookings.')}</Text>
              </div>
            ),
          }),
        },
      })}
    />
  );
};

const ExpandedRowElement = ({
  appointmentType,
  isOnlineSchedulingEnabled,
  canUseAppointmentColors,
  mappingList,
}: {
  appointmentType: AppointmentType;
  isOnlineSchedulingEnabled: boolean;
  canUseAppointmentColors: boolean;
  mappingList: Mapping[];
}) => {
  const { t } = useTranslation('scheduleAppointmentTypes');

  return (
    <CompactTable.ExpandedRow>
      {canUseAppointmentColors && (
        <CompactTable.InfoRow
          label={t('Color')}
          value={<ColorDot size='large' color={appointmentType?.details?.clr || PRIMARY_EVENT_COLOR} />}
        />
      )}
      <CompactTable.InfoRow
        label={t('Duration')}
        value={appointmentType.details?.dur ? `${appointmentType.details.dur} ${t('Minutes')}` : '-'}
      />
      <CompactTable.InfoRow
        label={t('Status')}
        value={
          <Chip variant={!!appointmentType.details?.act ? 'primary' : 'disabled'}>
            {!!appointmentType.details?.act ? t('Active') : t('Archived')}
          </Chip>
        }
      />
      <CompactTable.InfoRow
        label={t('Providers')}
        value={
          <ProvidersChip appointmentTypeId={appointmentType.id} mappingList={mappingList} css={styles.providersChip} />
        }
      />
      {isOnlineSchedulingEnabled && (
        <CompactTable.InfoRow
          label={t('Online Scheduling')}
          value={
            <VisibilityToggle
              appointmentTypeDetails={appointmentType}
              value={!!appointmentType.details?.sobs}
              disabled={!appointmentType.details?.act}
              trackingId={SETTINGS_TRACKING_IDS.appointmentTypes.onlineSchedulingToggleButton}
            />
          }
        />
      )}
    </CompactTable.ExpandedRow>
  );
};

const styles = {
  providersChip: css`
    justify-content: flex-end;
  `,
};
